// src/components/admin/AffiliateList.tsx
import React, { useEffect, useMemo, useState } from "react";

// ===== API ベース（他の管理画面と統一） =====
const RAW_API_BASE =
  (process as any).env?.REACT_APP_API_BASE_URL ||
  (import.meta as any).env?.VITE_API_BASE_URL ||
  "";

const API_BASE = RAW_API_BASE.replace(/\/+$/, "");
const API_ROOT = API_BASE ? `${API_BASE}/api` : "/api";

type Affiliate = {
  id: string;            // affiliates.json の id（uid文字列）
  affiliateId?: string;  // 媒体者ID（= groupId ベース）
  groupId?: string;
  groupName?: string;
  accountName: string;   // アカウント名（氏名欄に表示）
  email?: string;
  key?: string;          // 最新の鍵（互換用）
  keys?: string[];       // 複数鍵
  isActive?: boolean;
  createdAt?: string;
  note?: string;
  ownerUserId?: number;
};

export default function AffiliateList() {
  const [rows, setRows] = useState<Affiliate[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"all" | "active" | "locked">("all");
  const [loading, setLoading] = useState(false);
  const [newKey, setNewKey] = useState(""); // 登録用の鍵入力欄

  const adminHeaders: HeadersInit = {
    "Content-Type": "application/json",
    "x-api-key": "admin-dev",
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_ROOT}/admin/affiliates?query=${encodeURIComponent(
          query
        )}&status=${status}`,
        { headers: adminHeaders }
      );
      const d = await r.json();
      setRows(Array.isArray(d) ? d : []);
    } catch (e) {
      console.error(e);
      alert("一覧の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // 🔑 鍵登録：管理画面で入力した鍵をそのまま登録（グループ鍵）
  const registerGroupKey = async () => {
    const key = (newKey || "").trim();
    if (!key) {
      alert("登録する認証鍵を入力してください");
      return;
    }

    try {
      const r = await fetch(`${API_ROOT}/admin/group-keys`, {
        method: "POST",
        headers: adminHeaders,
        body: JSON.stringify({ key }),
      });

      if (!r.ok) {
        alert("鍵の登録に失敗しました");
        return;
      }

      // コピー用ダイアログ
      window.prompt("登録された認証用キー（コピーして共有）", key);
      setNewKey("");

      // ★ ここで一覧を再取得して「鍵済み」などを更新
      await fetchList();
    } catch (e) {
      console.error(e);
      alert("鍵の登録中にエラーが発生しました");
    }
  };

  // 行内：「鍵表示」＝紐づいている鍵一覧を表示するだけ
  const showKeys = (a: Affiliate) => {
    const list =
      (Array.isArray(a.keys) && a.keys.length > 0
        ? a.keys
        : a.key
        ? [a.key]
        : []) || [];

    if (!list.length) {
      alert("この媒体者にはまだ鍵が紐づいていません");
      return;
    }

    window.prompt("紐づいている認証キー（コピーして共有）", list.join("\n"));
  };

  const toggleActive = async (a: Affiliate) => {
    const r = await fetch(`${API_ROOT}/admin/affiliates/${a.id}`, {
      method: "PATCH",
      headers: adminHeaders,
      body: JSON.stringify({ isActive: !(a.isActive !== false) }),
    });
    if (!r.ok) return alert("更新に失敗しました");
    fetchList();
  };

  const editItem = async (a: Affiliate) => {
    const accountName =
      window.prompt("名前を編集", a.accountName || "") || "";
    if (!accountName) return;
    const email = window.prompt("メールを編集", a.email || "") || "";
    const note = window.prompt("メモ（任意）", a.note || "") || "";
    const r = await fetch(`${API_ROOT}/admin/affiliates/${a.id}`, {
      method: "PATCH",
      headers: adminHeaders,
      body: JSON.stringify({ accountName, email, note }),
    });
    if (!r.ok) return alert("更新に失敗しました");
    fetchList();
  };

  const delItem = async (id: string) => {
    if (!window.confirm("削除してよろしいですか？")) return;
    const r = await fetch(`${API_ROOT}/admin/affiliates/${id}`, {
      method: "DELETE",
      headers: adminHeaders,
    });
    if (!r.ok) return alert("削除に失敗しました");
    fetchList();
  };

  const fmtDate = (s?: string) => (s ? new Date(s).toLocaleString() : "-");
  const filteredCount = useMemo(() => rows.length, [rows]);

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <h2 style={{ margin: 0, fontWeight: 800 }}>アフィリエイト管理</h2>
        {/* 右上：鍵登録（左に入力欄＋右にボタン） */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="認証鍵を入力"
            style={inputKey}
          />
          <button onClick={registerGroupKey} style={btnPrimary}>
            🔑 鍵登録
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="検索（名前/メール/コード）"
          onKeyDown={(e) => e.key === "Enter" && fetchList()}
          style={input}
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as any)}
          style={select}
        >
          <option value="all">すべて</option>
          <option value="active">有効のみ</option>
          <option value="locked">鍵済みのみ</option>
        </select>
        <button onClick={fetchList} style={btnGhost}>
          {loading ? "更新中…" : "検索"}
        </button>
      </div>

      <div
        style={{
          fontSize: 12,
          color: "#64748b",
          marginBottom: 8,
        }}
      >
        {filteredCount} 件
      </div>

      <div
        style={{
          overflowX: "auto",
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              <th style={th}>ID</th>
              <th style={th}>氏名</th>
              <th style={th}>メール</th>
              <th style={th}>コード</th>
              <th style={th}>鍵</th>
              <th style={th}>状態</th>
              <th style={th}>登録日</th>
              <th style={th}>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const hasKey =
                (Array.isArray(a.keys) && a.keys.length > 0) || !!a.key;
              return (
                <tr key={a.id} style={{ borderTop: "1px solid #eef2f7" }}>
                  <td style={td}>{a.id}</td>
                  <td style={td}>{a.accountName || "-"}</td>
                  <td style={td}>{a.email || "-"}</td>
                  <td style={td} title="紹介リンクで使うコード">
                    {a.affiliateId || a.groupId || "-"}
                  </td>
                  <td style={td}>{hasKey ? "鍵済み" : "-"}</td>
                  <td style={td}>
                    {a.isActive === false ? "無効" : "有効"}
                  </td>
                  <td style={td}>{fmtDate(a.createdAt)}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <button onClick={() => showKeys(a)} style={btnSmall}>
                      鍵表示
                    </button>{" "}
                    <button onClick={() => editItem(a)} style={btnSmall}>
                      編集
                    </button>{" "}
                    <button onClick={() => delItem(a.id)} style={btnSmall}>
                      削除
                    </button>{" "}
                    <button onClick={() => toggleActive(a)} style={btnSmall}>
                      {a.isActive === false ? "有効化" : "無効化"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    padding: 16,
                    textAlign: "center",
                    color: "#94a3b8",
                  }}
                >
                  データがありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 12,
  color: "#334155",
};
const td: React.CSSProperties = {
  padding: "10px 12px",
  fontSize: 14,
  color: "#0f172a",
};
const input: React.CSSProperties = {
  flex: 1,
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
};
const inputKey: React.CSSProperties = {
  width: 180,
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
};
const select: React.CSSProperties = {
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  background: "#fff",
};
const btnGhost: React.CSSProperties = {
  padding: "10px 14px",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  background: "#f8fafc",
};
const btnPrimary: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  background: "#1976d2",
  color: "#fff",
  fontWeight: 800,
  border: 0,
};
const btnSmall: React.CSSProperties = {
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
};
